import type { Route } from "@route-types/+types/share.$pub";
import { ResponseType, webCoreContext } from "@sphtech/web2-core/ssr";
import { getArticleMetaCached } from "@src/ext/cueDigital/articleMetaCache";

import ArticleSharePage, {
  type ArticleShareLoaderData,
} from "../pages/ArticleShare";

const LOCALE_MONTHS: Record<string, string[]> = {
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  zh: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
  ms: [
    "Januari",
    "Februari",
    "Mac",
    "April",
    "Mei",
    "Jun",
    "Julai",
    "Ogos",
    "September",
    "Oktober",
    "November",
    "Disember",
  ],
  ta: [
    "ஜனவரி",
    "பிப்ரவரி",
    "மார்ச்",
    "ஏப்ரல்",
    "மே",
    "ஜூன்",
    "ஜூலை",
    "ஆகஸ்ட்",
    "செப்டம்பர்",
    "அக்டோபர்",
    "நவம்பர்",
    "டிசம்பர்",
  ],
};

const LOCALE_DATE_FORMAT: Record<string, string> = {
  en: "{{month}} {{date}}, {{year}}",
  zh: "{{year}} 年 {{month}} 月 {{date}} 日",
  ms: "{{month}} {{date}}, {{year}}",
  ta: "{{month}} {{date}}, {{year}}",
};

function formatDate(dateStr: string, lang: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const monthIndex = date.getMonth();
  const months = LOCALE_MONTHS[lang] ?? LOCALE_MONTHS.en;
  const format = LOCALE_DATE_FORMAT[lang] ?? LOCALE_DATE_FORMAT.en;

  return format
    .replace("{{month}}", months[monthIndex])
    .replace("{{date}}", String(date.getDate()))
    .replace("{{year}}", String(date.getFullYear()));
}

const BASE_URL =
  import.meta.env.VITE_SITE_BASE_URL ?? "https://e-paper.sph.com.sg";

type PubShareConfig = {
  siteName: string;
  title: string;
  description: string;
  lang: string;
  logoPath: string;
  brandColor: string;
  image?: string;
  url?: string;
};

const PUB_SHARE_CONFIG: Record<string, PubShareConfig> = {
  st: {
    siteName: "THE STRAITS TIMES",
    title: "Article - THE STRAITS TIMES",
    description: "Read the latest news from The Straits Times ePaper",
    lang: "en",
    logoPath: "/article_share/logos/st_logo.png",
    brandColor: "#1d315c",
  },
  bt: {
    siteName: "THE BUSINESS TIMES",
    title: "Article - THE BUSINESS TIMES",
    description: "Read the latest business news from The Business Times ePaper",
    lang: "en",
    logoPath: "/article_share/logos/bt_logo.png",
    brandColor: "#2b2b2b",
  },
  zb: {
    siteName: "联合早报",
    title: "文章 - 联合早报",
    description: "阅读联合早报电子报最新新闻",
    lang: "zh",
    logoPath: "/article_share/logos/zb_logo.png",
    brandColor: "#a80034",
  },
  bh: {
    siteName: "BERITA HARIAN",
    title: "Artikel - BERITA HARIAN",
    description: "Baca berita terkini dari ePaper Berita Harian",
    lang: "ms",
    logoPath: "/article_share/logos/bh_logo.svg",
    brandColor: "#447ab2",
  },
  tm: {
    siteName: "தமிழ் முரசு",
    title: "கட்டுரை - தமிழ் முரசு",
    description: "தமிழ் முரசு மின்னிதழில் சமீபத்திய செய்திகளைப் படிக்கவும்",
    lang: "ta",
    logoPath: "/article_share/logos/tm_logo.png",
    brandColor: "#b10442",
  },
  sm: {
    siteName: "新明日报",
    title: "文章 - 新明日报",
    description: "阅读新明日报电子报最新新闻",
    lang: "zh",
    logoPath: "/article_share/logos/sm_logo.png",
    brandColor: "#a80034",
  },
  tabla: {
    siteName: "TABLA!",
    title: "Article - TABLA!",
    description: "Read the latest news from TABLA! ePaper",
    lang: "en",
    logoPath: "/article_share/logos/tabla_logo.png",
    brandColor: "#b10442",
  },
  bm: {
    siteName: "BERITA MINGGU",
    title: "Artikel - BERITA MINGGU",
    description: "Baca berita terkini dari ePaper Berita Minggu",
    lang: "ms",
    logoPath: "/article_share/logos/bh_logo.svg",
    brandColor: "#b10442",
  },
  mm: {
    siteName: "மணிமேகலை",
    title: "கட்டுரை - மணிமேகலை",
    description: "மணிமேகலை மின்னிதழில் சமீபத்திய செய்திகளைப் படிக்கவும்",
    lang: "ta",
    logoPath: "/article_share/logos/tm_logo.png",
    brandColor: "#b10442",
  },
};

const DEFAULT_CONFIG: PubShareConfig = {
  siteName: "SPH E-Paper",
  title: "Article - SPH E-Paper",
  description:
    "Read the latest edition of your favourite SPH newspaper online.",
  lang: "en",
  logoPath: "/article_share/logos/st_logo.png",
  brandColor: "#000",
};

export function meta({ loaderData }: Route.MetaArgs) {
  const shareData = loaderData as { payload?: ArticleShareLoaderData };
  const { title, description, image, url, siteName } =
    shareData.payload ?? DEFAULT_CONFIG;

  return [
    { title },
    { name: "description", content: description },
    { name: "robots", content: "noindex, nofollow" },
    // Open Graph (Facebook, WhatsApp)
    { property: "og:type", content: "article" },
    { property: "og:url", content: url },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:site_name", content: siteName },
    ...(image ? [{ property: "og:image", content: image }] : []),
    // Twitter Card (X)
    {
      name: "twitter:card",
      content: image ? "summary_large_image" : "summary",
    },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    ...(image ? [{ name: "twitter:image", content: image }] : []),
  ];
}

export async function loader({ params, context }: Route.LoaderArgs) {
  const { pageLoaderResponse } = context.get(webCoreContext);

  if (pageLoaderResponse) {
    return pageLoaderResponse;
  }

  const pub = params.pub;
  const issueId = params.issueId;
  const issueDate = params.issueDate ?? "";
  const article = params.article ?? "";
  const config = PUB_SHARE_CONFIG[pub] ?? DEFAULT_CONFIG;

  // Reconstruct paths from params
  const issuePath = `sph/${pub}/issues/${issueId}`;
  const articleFileName = `${article}.html`;

  // Build the canonical share URL
  const shareUrl = `${BASE_URL}/share/${pub}/${issueId}/${issueDate}/${article}`;

  // Build the article iframe URL
  const articleUrl = `/ccidist-ws/${issuePath}/OPS/${articleFileName}`;

  // Fetch article metadata from CueDigital (server-side)
  let title = config.title;
  let description = config.description;
  let image: string | null = null;

  if (article && issueId) {
    const meta = await getArticleMetaCached(issuePath, articleFileName);
    if (meta.ogTitle) title = meta.ogTitle;
    if (meta.ogDescription) description = meta.ogDescription;
    if (meta.ogImage) image = meta.ogImage;
  }

  return {
    type: ResponseType.SUCCESS,
    statusCode: 200,
    payload: {
      title,
      description,
      image,
      url: shareUrl,
      siteName: config.siteName,
      lang: config.lang,
      articleUrl,
      issueDate: formatDate(issueDate, config.lang),
      logoPath: config.logoPath,
      brandColor: config.brandColor,
      pub,
    } satisfies ArticleShareLoaderData,
    lang: config.lang,
  };
}

export default function ShareRoute() {
  return <ArticleSharePage />;
}
